/**
 * Copyright (C) Mairistem
 *
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 *
 * Proprietary and confidential
 */

import _ from 'lodash';
import React from 'react';

import {
  Button,
  Label,
  Modal,
  type ModalProps,
  Segment,
} from '@mairistem/composants';

interface PanelModalEditionProps {
  children: React.ReactNode;
  errors: Record<string, string | boolean> & { api?: boolean };
  loading: boolean;
  header: string;
  updated?: boolean;
  scrollingContent?: boolean;
  'data-testid'?: string;
  onSave: () => void;
  onClose: () => void;
}

const PanelModalEdition = (props: PanelModalEditionProps & ModalProps) => {
  const {
    children,
    errors,
    loading,
    header,
    onSave,
    onClose,
    updated: _updated,
    scrollingContent = false,
    'data-testid': dataTestId,
    ...rest
  } = props;

  // Le préfixe sert aux boutons d'action ; la racine de la modale ne porte un
  // data-testid que si l'appelant en fournit un explicitement, afin de ne pas
  // introduire d'attribut là où il n'y en avait pas.
  const testIdPrefix = dataTestId ?? 'fiche-panel-modal-edition';

  const error: string = React.useMemo(() => {
    if (errors?.api) {
      return "Une erreur est survenue lors de l'enregistrement";
    }
    if (_.size(errors) === 1) {
      return _.values(errors)[0] as string;
    }
    if (_.size(errors) > 1) {
      return 'Une erreur est survenue, veuillez vérifier vos données.';
    }

    return null;
  }, [errors]);

  return (
    <Modal closeIcon open onClose={onClose} data-testid={dataTestId} {...rest}>
      <Modal.Header>{header}</Modal.Header>
      <Modal.Content scrolling={scrollingContent}>
        {!_.isNil(error) && (
          <Segment
            basic
            style={{
              marginLeft: '-0.5em',
              padding: 0,
            }}
          >
            <Label color="red" ribbon>
              {error}
            </Label>
          </Segment>
        )}
        {children}
      </Modal.Content>
      <Modal.Actions>
        <Button
          onClick={onClose}
          disabled={loading}
          basic
          size="small"
          icon="close"
          content="Annuler"
          data-testid={`${testIdPrefix}-annuler`}
        />
        <Button
          onClick={onSave}
          disabled={loading}
          loading={loading}
          positive
          icon="checkmark"
          content="Enregistrer"
          data-testid={`${testIdPrefix}-enregistrer`}
        />
      </Modal.Actions>
    </Modal>
  );
};

export default PanelModalEdition;
